// Import a clinic's own medicine list from CSV (FR-INV-2).
// Real lists come from Excel exports, WhatsApp and typed notes, so the parser is
// forgiving: it accepts a byte-order mark, either line ending, quoted fields with
// commas inside, and a range of column names in English or Burmese.

export interface ParsedDrug {
  row: number;
  generic_name: string;
  brand_name: string | null;
  manufacturer: string | null;
  strength: string | null;
  form: string | null;
  dispense_unit: string;
  selling_price: number;
  reorder_level: number;
  errors: string[];
}

/** Split CSV text into rows of fields, honouring quotes and embedded newlines. */
export function parseCsv(text: string): string[][] {
  const src = text.replace(/^﻿/, '');
  const rows: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQuotes = false;
  for (let i = 0; i < src.length; i++) {
    const c = src[i]!;
    if (inQuotes) {
      if (c === '"') {
        if (src[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += c;
      continue;
    }
    if (c === '"') { inQuotes = true; continue; }
    if (c === ',' || c === '\t') { row.push(field); field = ''; continue; }
    if (c === '\r') continue;
    if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; continue; }
    field += c;
  }
  if (field !== '' || row.length > 0) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((f) => f.trim() !== ''));
}

const ALIASES: Record<keyof Omit<ParsedDrug, 'row' | 'errors'>, string[]> = {
  generic_name: ['generic', 'generic name', 'generic_name', 'genericname', 'drug', 'drug name', 'medicine', 'name', 'ဆေးအမည်', 'ပိုဒ်အမည်'],
  brand_name: ['brand', 'brand name', 'brand_name', 'brandname', 'trade', 'trade name', 'ကုန်အမှတ်တံဆိပ်'],
  manufacturer: ['manufacturer', 'maker', 'company', 'mfg', 'ကုမ္ပဏီ'],
  strength: ['strength', 'dose', 'dosage', 'mg', 'ပမာဏ'],
  form: ['form', 'type', 'dosage form', 'ပုံစံ'],
  dispense_unit: ['unit', 'dispense unit', 'dispense_unit', 'uom', 'ယူနစ်'],
  selling_price: ['price', 'selling price', 'selling_price', 'sale price', 'unit price', 'mmk', 'ks', 'ဈေးနှုန်း', 'ဈေး'],
  reorder_level: ['reorder', 'reorder level', 'reorder_level', 'min', 'minimum', 'min stock'],
};

/** Map the header row onto our fields. Unknown columns are ignored. */
export function mapHeaders(header: string[]): Partial<Record<keyof typeof ALIASES, number>> {
  const out: Partial<Record<keyof typeof ALIASES, number>> = {};
  header.forEach((raw, i) => {
    // "Selling Price (MMK)" and "Price [Ks]" mean the same column as "price".
    const h = raw.trim().toLowerCase().replace(/[([][^)\]]*[)\]]/g, ' ').replace(/\s+/g, ' ').trim();
    for (const [field, names] of Object.entries(ALIASES) as [keyof typeof ALIASES, string[]][]) {
      if (out[field] === undefined && names.includes(h)) out[field] = i;
    }
  });
  return out;
}

/** Accepts "1,200", "1200 Ks", "၁၂၀၀"; returns null when there is no number. */
export function parsePrice(raw: string): number | null {
  const ascii = raw.replace(/[၀-၉]/g, (d) => String('၀၁၂၃၄၅၆၇၈၉'.indexOf(d)));
  const cleaned = ascii.replace(/[^0-9.]/g, '');
  if (cleaned === '' || cleaned === '.') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? Math.round(n) : null;
}

const FORM_UNIT: Record<string, string> = {
  tablet: 'tablet', tab: 'tablet', capsule: 'capsule', cap: 'capsule', syrup: 'ml', suspension: 'ml',
  injection: 'vial', vial: 'vial', ampoule: 'ampoule', cream: 'tube', ointment: 'tube', gel: 'tube',
  drops: 'bottle', inhaler: 'inhaler', sachet: 'sachet', patch: 'patch',
};

export function parseDrugCsv(text: string): { rows: ParsedDrug[]; headerFound: boolean; missing: string[] } {
  const raw = parseCsv(text);
  if (raw.length === 0) return { rows: [], headerFound: false, missing: ['generic_name'] };

  const cols = mapHeaders(raw[0]!);
  const headerFound = cols.generic_name !== undefined || cols.brand_name !== undefined;
  // With no recognisable header, assume: generic, brand, strength, form, price.
  const idx = headerFound ? cols : { generic_name: 0, brand_name: 1, strength: 2, form: 3, selling_price: 4 };
  const body = headerFound ? raw.slice(1) : raw;

  const missing: string[] = [];
  if (idx.generic_name === undefined && idx.brand_name === undefined) missing.push('generic_name');
  if (idx.selling_price === undefined) missing.push('selling_price');

  const get = (r: string[], i?: number) => (i === undefined ? '' : (r[i] ?? '').trim());

  const rows = body.map((r, n) => {
    const errors: string[] = [];
    const generic = get(r, idx.generic_name);
    const brand = get(r, idx.brand_name);
    const name = generic || brand;
    if (!name) errors.push('no medicine name');

    const priceRaw = get(r, idx.selling_price);
    const price = parsePrice(priceRaw);
    if (price === null) errors.push('no price');

    const form = get(r, idx.form).toLowerCase();
    const unit = get(r, idx.dispense_unit) || FORM_UNIT[form] || 'tablet';

    return {
      row: n + (headerFound ? 2 : 1),
      generic_name: generic || brand,
      brand_name: brand && generic ? brand : null,
      manufacturer: get(r, idx.manufacturer) || null,
      strength: get(r, idx.strength) || null,
      form: get(r, idx.form) || null,
      dispense_unit: unit,
      selling_price: price ?? 0,
      reorder_level: parsePrice(get(r, idx.reorder_level)) ?? 0,
      errors,
    } satisfies ParsedDrug;
  });

  return { rows, headerFound, missing };
}
